import React from 'react';
import moment from 'moment';
import 'moment/locale/es-mx';
import { openOrFocusTicketTab, setActiveTicketTab } from '../../../../../actions/ui';

moment.locale('es-mx');

export const useTaskDetailScreenState = ({
    dispatch,
    navigate,
    location,
    ticketId,
    ticket,
    follows,
    followSortOrder,
    activeFollowTab,
    normalizedTicketTabs,
}) => {
    React.useEffect(() => {
        if (!ticket?._id) {
            return;
        }

        dispatch(openOrFocusTicketTab({
            id: ticket._id,
            code: ticket.code,
            title: ticket.title,
        }));
    }, [dispatch, ticket?._id, ticket?.code, ticket?.title]);

    React.useEffect(() => {
        if (!ticketId) {
            return;
        }

        const exists = normalizedTicketTabs.some((tab) => tab.id === String(ticketId));
        if (!exists) {
            return;
        }

        dispatch(setActiveTicketTab(ticketId));
    }, [dispatch, normalizedTicketTabs, ticketId]);

    React.useEffect(() => {
        const currentTicketCode = String(ticket?.code || '').trim();
        const stateTicketCode = String(location.state?.ticketCode || '').trim();

        if (!currentTicketCode || currentTicketCode === stateTicketCode) {
            return;
        }

        navigate(location.pathname, {
            replace: true,
            state: {
                ...(location.state || {}),
                ticketCode: currentTicketCode,
            },
        });
    }, [ticket?.code, location.pathname, location.state, navigate]);

    const sortedFollows = React.useMemo(() => {
        const safeFollows = Array.isArray(follows) ? [...follows] : [];
        const resolveTime = (follow) => {
            const rawValue = follow?.createdDate || follow?.updatedDate || follow?.createdAt || follow?.updatedAt;
            const timestamp = rawValue ? new Date(rawValue).getTime() : 0;
            return Number.isFinite(timestamp) ? timestamp : 0;
        };

        safeFollows.sort((left, right) => {
            const leftTime = resolveTime(left);
            const rightTime = resolveTime(right);
            return followSortOrder === 'ASC' ? leftTime - rightTime : rightTime - leftTime;
        });

        return safeFollows;
    }, [follows, followSortOrder]);

    const publicFollows = React.useMemo(() => {
        return sortedFollows.filter((follow) => follow?.isPublic === true);
    }, [sortedFollows]);

    const privateFollows = React.useMemo(() => {
        return sortedFollows.filter((follow) => follow?.isPublic === false);
    }, [sortedFollows]);

    const visibleFollows = React.useMemo(() => {
        if (activeFollowTab === 'public') {
            return publicFollows;
        }

        if (activeFollowTab === 'private') {
            return privateFollows;
        }

        return sortedFollows;
    }, [activeFollowTab, publicFollows, privateFollows, sortedFollows]);

    const followEmptyMessage = React.useMemo(() => {
        if (activeFollowTab === 'public') {
            return 'No hay notas publicas aun.';
        }

        if (activeFollowTab === 'private') {
            return 'No hay notas privadas aun.';
        }

        return 'No hay notas aun.';
    }, [activeFollowTab]);

    const dueFromNow = React.useMemo(() => {
        const ticketDueDate = ticket?.resolutionDueDate || ticket?.firstResponseDueDate;
        return ticketDueDate ? moment(ticketDueDate).fromNow() : '-';
    }, [ticket?.resolutionDueDate, ticket?.firstResponseDueDate]);

    return {
        sortedFollows,
        publicFollows,
        privateFollows,
        visibleFollows,
        followEmptyMessage,
        dueFromNow,
    };
};
